import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { DnDProvider, useDnD } from '../context/DnDContext';
import { useEtlAuth as useAuth } from '../hooks/useEtlAuth';
import { useTheme } from '../../../context/ThemeContext';
import { useFlowStore } from '../store/flowStore';
import { TurboNode } from '../components/nodes/TurboNode';
import GatewayNode from '../components/nodes/GatewayNode';
import { TurboEdge } from '../components/edges/TurboEdge';
import { Sidebar } from '../components/Sidebar';
import { ConfigPanel } from '../components/ConfigPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast, Toaster } from 'sonner';
import { MqttBrokerModal } from '../components/modals/MqttBrokerModal';
import {
  getMqttConfigFromGateways,
  getGatewayMqttTopic,
  mqttConfigForStorage,
  subscribeFlowInputTopics,
  isMqttConfigComplete,
} from '../utils/mqttDefaults';
import {
  resolveGatewayNodeData,
  prepareCanvasForRun,
} from '../utils/pipelineUpstream';
import {
  findAccountGateway,
  resolveGatewayNodeStatus,
} from '../utils/gatewayStatus';
import { checkMqttBrokerHealth } from '../utils/mqttConnect';
import { 
  LuWifi, 
  LuWifiOff, 
  LuSave, 
  LuDownload, 
  LuPlay,
  LuSquare,
  LuCopy,
  LuCheck,
  LuSun,
  LuMoon,
  LuRadio,
  LuX,
  LuUndo2,
  LuRedo2,
  LuFolderOpen,
  LuUpload,
  LuTable,
  LuBraces,
  LuTrash2,
  LuLogOut,
  LuUser,
  LuPencil,
  LuFilePlus,
  LuArrowLeft,
  LuLoader,
} from 'react-icons/lu';
import { SiMqtt } from 'react-icons/si';
import { LiveDataTable } from '../components/LiveDataTable';
import { ETL_API, getEtlWsBase } from '../../../config/etlApi';
import {
  getPipeline,
  listPipelines,
  createPipeline,
  updatePipeline,
  deletePipeline,
} from '../../../services/pipelineService';
import { enrichNodeConfig, sanitizeNodeConfig, getNodeDragPayload } from '../utils/nodeMeta';
import { hasConfiguredAi } from '../../../config/aiConfig';

const API = ETL_API;

const nodeTypes = { turbo: TurboNode, gateway: GatewayNode };
const edgeTypes = { turbo: TurboEdge };

const defaultEdgeOptions = {
  type: 'turbo',
  markerEnd: 'edge-circle',
  animated: false,
};

let nodeId = 0;
const getId = () => `node_${nodeId++}`;

// Custom hook for mobile detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

function Flow({ embedded = false, flowId: routeFlowId, onBack }) {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const edgesRef = useRef(edges);
  edgesRef.current = edges;
  const [selectedNode, setSelectedNode] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [flowName, setFlowName] = useState('Untitled Flow');
  const [flowId, setFlowId] = useState(routeFlowId || null);
  const [isEditingName, setIsEditingName] = useState(!routeFlowId);
  const [flowLoading, setFlowLoading] = useState(!!routeFlowId);
  const [mqttModalOpen, setMqttModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [liveViewOpen, setLiveViewOpen] = useState(false);
  const [mqttConnected, setMqttConnected] = useState(false);
  const [mqttBrokerOnline, setMqttBrokerOnline] = useState(false);
  const [mqttConfig, setMqttConfig] = useState({ host: '', port: 1883, username: '', password: '', useTLS: false });
  const [isExecuting, setIsExecuting] = useState(false);
  const [isStartingFlow, setIsStartingFlow] = useState(false);
  const [copied, setCopied] = useState(false);
  const [theme, setTheme] = useState('light');
  const { theme: appTheme } = useTheme();
  const activeTheme = embedded ? appTheme : theme;
  const [liveMessages, setLiveMessages] = useState([]);
  const [liveViewMode, setLiveViewMode] = useState('json'); // 'json' or 'table'
  const [generatedScript, setGeneratedScript] = useState('');
  const [schemas, setSchemas] = useState({});
  const [activeNodes, setActiveNodes] = useState({});  // node_id -> activity state
  const [savedFlows, setSavedFlows] = useState([]);
  const [loadFlowModalOpen, setLoadFlowModalOpen] = useState(false);
  const [nodeErrors, setNodeErrors] = useState({});  // node_id -> error message
  const [gatewaysOnCanvas, setGatewaysOnCanvas] = useState(new Set()); // Track which gateways are on canvas
  const wsRef = useRef(null);
  const liveViewOpenRef = useRef(false);
  const onBackRef = useRef(onBack);
  const pipelineLoadedRef = useRef(false);
  const autosaveTimerRef = useRef(null);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  // Keep a ref of the Live panel toggle so the WebSocket handler (which must not
  // depend on liveViewOpen to avoid reconnect churn) always reads the latest value.
  useEffect(() => {
    liveViewOpenRef.current = liveViewOpen;
  }, [liveViewOpen]);

  // Radix Select portals mount on document.body — sync theme for dropdown surfaces.
  useEffect(() => {
    document.body.setAttribute('data-etl-select-theme', activeTheme);
    return () => {
      document.body.removeAttribute('data-etl-select-theme');
    };
  }, [activeTheme]);
  
  const { screenToFlowPosition } = useReactFlow();
  const [type, setType, nodeData, setNodeData] = useDnD();
  const isMobile = useIsMobile();
  const { updateNode, deleteNode } = useFlowStore();
  const { user, gateways, logout, isAuthenticated, loading: authLoading, refreshGateways } = useAuth();
  const gatewaysRef = useRef(gateways);
  gatewaysRef.current = gateways;

  const applyFlowData = useCallback((flow) => {
    setFlowName(flow.name);
    setFlowId(flow.id || flow._id || null);
    setIsEditingName(false);
    const loadedNodes = (flow.nodes || []).map((n) => {
      const raw = n.config || n.data || {};
      const enriched = enrichNodeConfig(raw);
      const resolved =
        enriched.nodeType === 'kGateway' || n.type === 'gateway'
          ? resolveGatewayNodeData(enriched, gatewaysRef.current)
          : enriched;
      return {
        id: n.node_id || n.id,
        type: n.type || (resolved.nodeType === 'kGateway' ? 'gateway' : 'turbo'),
        position: n.position || { x: 0, y: 0 },
        data: {
          ...resolved,
          title: resolved.name || resolved.label || resolved.title,
        },
      };
    });
    const loadedEdges = flow.edges || [];
    const prepared = prepareCanvasForRun(loadedNodes, loadedEdges, gatewaysRef.current);
    setNodes(prepared.nodes);
    setEdges(prepared.edges);
  }, [setNodes, setEdges]);

  // Client-side fallback until modal loads from Netlify/Supabase
  useEffect(() => {
    if (!isAuthenticated || flowLoading) return;
    setMqttConfig(getMqttConfigFromGateways(gateways));
  }, [isAuthenticated, gateways, flowLoading]);

  const openMqttModal = useCallback(() => {
    setMqttModalOpen(true);
  }, []);

  // Poll MQTT broker health (Cloud Run session or Netlify health check)
  useEffect(() => {
    if (!isAuthenticated) {
      setMqttBrokerOnline(false);
      setMqttConnected(false);
      return undefined;
    }

    const refreshMqttStatus = async () => {
      try {
        const health = await checkMqttBrokerHealth();
        setMqttBrokerOnline(!!health.brokerReachable);
        if (health.online || health.connected) {
          setMqttConnected(true);
        } else if (!health.brokerReachable) {
          setMqttConnected(false);
        }
      } catch {
        setMqttBrokerOnline(false);
      }
    };

    refreshMqttStatus();
    const interval = setInterval(refreshMqttStatus, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, gateways]);

  useEffect(() => {
    if (!routeFlowId) return undefined;

    let cancelled = false;
    pipelineLoadedRef.current = false;

    const loadFlow = async () => {
      setFlowLoading(true);
      try {
        const flow = await getPipeline(routeFlowId);
        if (!cancelled) {
          applyFlowData(flow);
          pipelineLoadedRef.current = true;
        }
      } catch {
        if (!cancelled) {
          toast.error('Pipeline not found');
          onBackRef.current?.();
        }
      } finally {
        if (!cancelled) {
          setFlowLoading(false);
        }
      }
    };

    loadFlow();
    return () => {
      cancelled = true;
    };
  }, [routeFlowId, applyFlowData]);

  const buildFlowPayload = useCallback(() => ({
    name: flowName,
    nodes: nodes.map((n) => ({
      node_id: n.id,
      type: n.type,
      position: n.position,
      config: sanitizeNodeConfig(n.data),
    })),
    edges,
    mqtt_config: mqttConfigForStorage(mqttConfig),
  }), [flowName, nodes, edges, mqttConfig]);

  // Autosave canvas changes to Supabase (debounced)
  useEffect(() => {
    if (!routeFlowId || !pipelineLoadedRef.current || flowLoading) return undefined;

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(async () => {
      try {
        await updatePipeline(routeFlowId, buildFlowPayload());
      } catch {
        // Silent — user can still use manual save
      }
    }, 1500);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [routeFlowId, nodes, edges, flowName, mqttConfig, flowLoading, buildFlowPayload]);

  // Auto-place account gateways on canvas (functional update avoids stale nodes).
  useEffect(() => {
    if (!isAuthenticated || !gateways.length) return;

    setNodes((prevNodes) => {
      const existingGatewayIds = new Set();
      prevNodes.forEach((n) => {
        if (n.type === 'gateway' || n.data?.nodeType === 'kGateway') {
          if (n.data?.gatewayId) existingGatewayIds.add(String(n.data.gatewayId));
          if (n.data?.id) existingGatewayIds.add(String(n.data.id));
        }
      });

      const newGateways = gateways.filter((gw) => !existingGatewayIds.has(String(gw.id)));
      const macClean = (mac) => String(mac || '').replace(/[^0-9A-Fa-f]/g, '').toUpperCase();

      const newNodes = newGateways.map((gateway, index) => ({
        id: macClean(gateway.mac_address || gateway.gateway_mac_hex)
          ? `gateway_${macClean(gateway.mac_address || gateway.gateway_mac_hex)}`
          : `gateway_${gateway.id}`,
        type: 'gateway',
        position: { x: 50, y: 50 + (index * 200) },
        data: {
          nodeType: 'kGateway',
          gatewayId: gateway.id,
          id: gateway.id,
          name: gateway.name,
          mac_address: gateway.mac_address || gateway.gateway_mac_hex,
          status: gateway.status,
          mqtt_broker_host: gateway.mqtt_broker_host,
          mqtt_broker_port: gateway.mqtt_broker_port,
          mqtt_username: gateway.mqtt_username,
          mqtt_password: gateway.mqtt_password,
          tbmq_username: gateway.tbmq_username,
          tbmq_password: gateway.tbmq_password,
          mqtt_topic: gateway.mqtt_topic,
          mqtt_client_id: gateway.mqtt_client_id,
          isConnected: false,
          title: gateway.name,
          label: gateway.name,
        },
      }));

      if (newNodes.length > 0) {
        toast.success(`${newNodes.length} gateway(s) loaded from your account`);
        setGatewaysOnCanvas((prev) => new Set([...prev, ...newGateways.map((g) => g.id)]));
      }

      const prepared = prepareCanvasForRun(
        newNodes.length ? [...prevNodes, ...newNodes] : prevNodes,
        edgesRef.current,
        gateways,
      );
      if (JSON.stringify(prepared.edges) !== JSON.stringify(edgesRef.current)) {
        setEdges(prepared.edges);
      }
      return JSON.stringify(prepared.nodes) === JSON.stringify(prevNodes) ? prevNodes : prepared.nodes;
    });
  }, [isAuthenticated, gateways, setEdges]);

  // Keep canvas gateway nodes in sync with account gateway records (MAC, topic, creds).
  useEffect(() => {
    if (!gateways?.length) return;
    setNodes((prevNodes) => {
      const prepared = prepareCanvasForRun(prevNodes, edgesRef.current, gateways);
      if (JSON.stringify(prepared.edges) !== JSON.stringify(edgesRef.current)) {
        setEdges(prepared.edges);
      }
      return JSON.stringify(prepared.nodes) === JSON.stringify(prevNodes) ? prevNodes : prepared.nodes;
    });
  }, [gateways, setEdges]);

  const syncGatewayNodeStatuses = useCallback((messageSnapshot = liveMessages) => {
    setNodes((prev) => {
      let changed = false;
      const next = prev.map((node) => {
        if (node.type !== 'gateway' && node.data?.nodeType !== 'kGateway') return node;

        const accountGateway = findAccountGateway(gateways, {
          gatewayId: node.data?.gatewayId || node.data?.id,
          macAddress: node.data?.mac_address,
        });
        const mqttTopic = getGatewayMqttTopic(node.data || {});
        const status = resolveGatewayNodeStatus({
          accountGateway,
          liveMessages: messageSnapshot,
          macAddress: node.data?.mac_address,
          mqttTopic,
          fallbackStatus: accountGateway?.status || 'offline',
        });

        if (node.data?.status === status) return node;
        changed = true;
        return { ...node, data: { ...node.data, status } };
      });
      return changed ? next : prev;
    });

    setSelectedNode((prev) => {
      if (!prev || (prev.type !== 'gateway' && prev.data?.nodeType !== 'kGateway')) return prev;
      const accountGateway = findAccountGateway(gateways, {
        gatewayId: prev.data?.gatewayId || prev.data?.id,
        macAddress: prev.data?.mac_address,
      });
      const mqttTopic = getGatewayMqttTopic(prev.data || {});
      const status = resolveGatewayNodeStatus({
        accountGateway,
        liveMessages: messageSnapshot,
        macAddress: prev.data?.mac_address,
        mqttTopic,
        fallbackStatus: accountGateway?.status || 'offline',
      });
      if (prev.data?.status === status) return prev;
      return { ...prev, data: { ...prev.data, status } };
    });
  }, [gateways, liveMessages, setNodes]);

  // Keep canvas gateway nodes in sync with account + live MQTT data.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    syncGatewayNodeStatuses();
  }, [isAuthenticated, gateways, liveMessages, syncGatewayNodeStatuses]);

  // Refresh gateway statuses periodically (same source as Gateway List page).
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    refreshGateways();
    const interval = setInterval(() => {
      refreshGateways();
    }, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshGateways]);

  // Handle gateway connection when executing flow
  const connectGateway = async (gatewayData) => {
    try {
      const response = await fetch(`${API}/mqtt/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host: gatewayData.mqtt_broker_host,
          port: gatewayData.mqtt_broker_port,
          username: gatewayData.tbmq_username,
          password: gatewayData.tbmq_password,
          client_id: `etl_${gatewayData.mqtt_client_id || gatewayData.mac_address}`,
        }),
      });
      
      if (response.ok) {
        // Update gateway node to show connected
        setNodes(prev => prev.map(node => 
          node.id === `gateway_${gatewayData.mac_address}`
            ? { ...node, data: { ...node.data, isConnected: true } }
            : node
        ));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Gateway connection error:', error);
      return false;
    }
  };

  // Auto-collapse on mobile
  useEffect(() => {
    setSidebarCollapsed(isMobile);
  }, [isMobile]);

  // Theme toggle
  const toggleTheme = () => {
    if (embedded) return;
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  useEffect(() => {
    if (embedded) return undefined;
    document.documentElement.setAttribute('data-theme', theme);
    return () => {
      document.documentElement.removeAttribute('data-theme');
    };
  }, [embedded, theme]);

  // AI Code Generation handlers
  const generateAICode = async (nodeId, nodeConfig) => {
    if (!hasConfiguredAi(nodeConfig) || !nodeConfig.aiPrompt) {
      toast.error('Please fill in all AI configuration fields');
      return;
    }
    
    try {
      toast.loading('Generating code with AI...', { id: 'ai-gen' });
      
      const response = await fetch(`${API}/ai/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: nodeConfig.aiProvider,
          api_key: nodeConfig.aiApiKey,
          mode: 'function_code',
          prompt: nodeConfig.aiPrompt
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate code');
      }
      
      const result = await response.json();
      
      // Update node with generated code
      handleNodeUpdate(nodeId, { ...nodeConfig, code: result.generated });
      toast.success('Code generated!', { id: 'ai-gen' });
      
    } catch (error) {
      toast.error(`AI Error: ${error.message}`, { id: 'ai-gen' });
    }
  };

  const generateAITransform = async (nodeId, nodeConfig) => {
    if (!hasConfiguredAi(nodeConfig)) {
      toast.error('Please fill in AI provider and API key');
      return;
    }
    
    try {
      toast.loading('Generating with AI...', { id: 'ai-transform' });
      
      const requestBody = {
        provider: nodeConfig.aiProvider,
        api_key: nodeConfig.aiApiKey,
        mode: nodeConfig.mode || 'ble',
        manufacturer_docs: nodeConfig.manufacturerDocs,
        example_payload: nodeConfig.examplePayload,
        expected_output: nodeConfig.expectedOutput,
        target_db: nodeConfig.targetDb,
        table_name: nodeConfig.tableName,
        current_schema: nodeConfig.currentSchema,
        prompt: nodeConfig.customInstructions,
        sample_messages: liveMessages.slice(0, 10)
      };
      
      const response = await fetch(`${API}/ai/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate');
      }
      
      const result = await response.json();
      
      // Update node with generated rules
      handleNodeUpdate(nodeId, { ...nodeConfig, generatedRules: result.generated });
      toast.success('Generated successfully!', { id: 'ai-transform' });
      
    } catch (error) {
      toast.error(`AI Error: ${error.message}`, { id: 'ai-transform' });
    }
  };

  // AI BLE sample analysis
  const analyzeBleSample = async (nodeId, nodeConfig, deviceType) => {
    if (!hasConfiguredAi(nodeConfig) || !nodeConfig.samplePayload) {
      toast.error('Please fill in AI provider, API key, and sample payload');
      return;
    }
    
    try {
      toast.loading('Analyzing BLE payload with AI...', { id: 'ai-ble' });
      
      const response = await fetch(`${API}/ai/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: nodeConfig.aiProvider,
          api_key: nodeConfig.aiApiKey,
          mode: 'ble_decoder',
          example_payload: nodeConfig.samplePayload,
          manufacturer_docs: `Device type: ${deviceType}. Analyze this BLE advertisement data and provide byte mapping rules.`
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to analyze');
      }
      
      const result = await response.json();
      toast.success('Analysis complete! Check console for decoder suggestions.', { id: 'ai-ble' });
      console.log('AI BLE Analysis:', result.generated);
      
      // Show in a modal or update config
      handleNodeUpdate(nodeId, { ...nodeConfig, aiAnalysis: result.generated });
      
    } catch (error) {
      toast.error(`AI Error: ${error.message}`, { id: 'ai-ble' });
    }
  };

  // Expose AI functions to window for ConfigPanel access
  useEffect(() => {
    window.generateAICode = generateAICode;
    window.generateAITransform = generateAITransform;
    window.analyzeBleSample = analyzeBleSample;
    return () => {
      delete window.generateAICode;
      delete window.generateAITransform;
      delete window.analyzeBleSample;
    };
  }, [liveMessages]);

  // WebSocket for live messages and node activity
  useEffect(() => {
    if (mqttConnected) {
      const wsUrl = `${getEtlWsBase()}/api/ws/messages`;
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket connected for live messages');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'message') {
            // Always buffer recent messages so the gateway Data tab and View nodes
            // can show data even when the global Live panel is closed.
            setLiveMessages(prev => [data.data, ...prev].slice(0, 50));
          } else if (data.type === 'node_activity') {
            // Handle node activity for visual feedback
            const { node_id, activity } = data;
            setActiveNodes(prev => ({ ...prev, [node_id]: activity }));
            
            // Clear activity after animation
            setTimeout(() => {
              setActiveNodes(prev => {
                const newState = { ...prev };
                if (newState[node_id] === activity) {
                  delete newState[node_id];
                }
                return newState;
              });
            }, 500);
            
            // Track errors
            if (activity === 'error') {
              setNodeErrors(prev => ({ ...prev, [node_id]: 'Processing error' }));
            } else if (activity === 'success') {
              setNodeErrors(prev => {
                const newState = { ...prev };
                delete newState[node_id];
                return newState;
              });
            }
          }
        } catch (e) {
          console.error('WebSocket message error:', e);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      ws.onclose = () => {
        console.log('WebSocket closed');
      };
      
      wsRef.current = ws;
      
      return () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      };
    }
  }, [mqttConnected]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, ...defaultEdgeOptions }, eds)),
    []
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      let dropType = type;
      let dropData = nodeData;

      if (!dropType || !dropData) {
        try {
          const raw = event.dataTransfer.getData('application/etl-node');
          if (raw) {
            const parsed = JSON.parse(raw);
            dropType = parsed.type;
            dropData = parsed;
          }
        } catch {
          // ignore parse errors
        }
      }

      if (!dropType || !dropData) return;

      const enriched = enrichNodeConfig(dropData);
      if (enriched.nodeType === 'kGateway') {
        toast.info('Gateways are added automatically from your account — connect the gateway node already on the canvas');
        return;
      }

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = {
        id: getId(),
        type: dropType,
        position,
        data: {
          ...enriched,
          title: enriched.name || enriched.label || enriched.title,
        },
      };

      setNodes((nds) => nds.concat(newNode));
      setType(null);
      setNodeData(null);
    },
    [screenToFlowPosition, type, nodeData, setNodes, setType, setNodeData]
  );

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // Save Flow
  const handleSave = async () => {
    try {
      const flowData = buildFlowPayload();
      const id = routeFlowId || flowId;
      const savedFlow = id
        ? await updatePipeline(id, flowData)
        : await createPipeline(flowData);
      if (!id) {
        setFlowId(savedFlow.id);
      }
      toast.success('Flow saved');
    } catch (e) {
      toast.error('Save failed');
    }
  };

  // Export JSON
  const exportJSON = () => {
    return JSON.stringify({
      name: flowName,
      nodes: nodes.map(n => ({
        node_id: n.id,
        type: n.data.nodeType,
        position: n.position,
        config: n.data,
      })),
      edges,
      mqtt_config: mqttConfigForStorage(mqttConfig),
    }, null, 2);
  };

  // Execute Flow
  const handleExecute = async (canvasNodes = nodes, canvasEdges = edges) => {
    if (isStartingFlow) return;

    if (isExecuting) {
      // Stop flow
      try {
        await fetch(`${API}/flow/stop`, { method: 'POST' });
        setIsExecuting(false);
        setEdges(eds => eds.map(e => ({ ...e, animated: false })));
        toast.info('Flow stopped');
      } catch (e) {
        toast.error(`Failed to stop flow: ${e.message || 'network error'}`);
      }
      return;
    }

    const effectiveMqttConfig = isMqttConfigComplete(mqttConfig)
      ? mqttConfig
      : getMqttConfigFromGateways(gateways);

    // Start flow. The backend connects MQTT itself from mqtt_config (so the same
    // instance that runs the graph holds the connection), but we still send full
    // credentials here — mqttConfigForStorage strips them for persisted saves only.
    setIsStartingFlow(true);
    try {
      // Prime topics on the backend session as well (idempotent with start()).
      await subscribeFlowInputTopics(
        canvasNodes.map((n) => {
          if (n.data?.nodeType === 'kGateway' || n.type === 'gateway') {
            return { ...n, data: resolveGatewayNodeData(n.data || {}, gateways) };
          }
          return n;
        }),
        API,
      );

      const flowData = {
        name: flowName,
        nodes: canvasNodes.map(n => {
          const data =
            n.data?.nodeType === 'kGateway' || n.type === 'gateway'
              ? resolveGatewayNodeData(n.data || {}, gateways)
              : n.data;
          return {
            node_id: n.id,
            type: n.type,
            position: n.position,
            config: data,
            outputs: canvasEdges.filter(e => e.source === n.id).map(e => e.target),
          };
        }),
        edges: canvasEdges,
        mqtt_config: {
          host: effectiveMqttConfig.host,
          port: effectiveMqttConfig.port,
          username: effectiveMqttConfig.username,
          password: effectiveMqttConfig.password,
          useTLS: effectiveMqttConfig.useTLS,
        },
      };

      const response = await fetch(`${API}/flow/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(flowData),
      });

      if (response.ok) {
        // The backend now guarantees a broker connection for this run; reflect it
        // locally so the Live panel opens its WebSocket without a manual connect.
        if (isMqttConfigComplete(effectiveMqttConfig)) {
          setMqttConfig(effectiveMqttConfig);
        }
        setMqttConnected(true);
        setMqttBrokerOnline(true);
        setIsExecuting(true);
        setLiveViewOpen(true);
        setEdges(eds => eds.map(e => ({ ...e, animated: true })));
        toast.success('Flow running — streaming MQTT data through the pipeline');
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(`Failed to start: ${error.detail || response.statusText}`);
      }
    } catch (e) {
      toast.error(`Failed to start flow: ${e.message || 'network error'}`);
    } finally {
      setIsStartingFlow(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(exportJSON());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  const handleCopyScript = () => {
    navigator.clipboard.writeText(generatedScript);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Script copied to clipboard');
  };

  // Generate Node.js script
  const handleGenerateScript = async () => {
    try {
      const flowData = {
        flow: {
          name: flowName,
          nodes: nodes.map(n => ({
            node_id: n.id,
            type: n.type,
            position: n.position,
            config: n.data,
          })),
          edges,
        },
        mqtt_config: mqttConfigForStorage(mqttConfig),
      };
      
      const response = await fetch(`${API}/codegen/nodejs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(flowData),
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeneratedScript(data.script);
        toast.success('Node.js script generated');
      } else {
        toast.error('Failed to generate script');
      }
    } catch (e) {
      toast.error('Failed to generate script');
    }
  };

  // Fetch discovered schemas
  const handleFetchSchemas = async () => {
    try {
      const response = await fetch(`${API}/schema/discover`);
      if (response.ok) {
        const data = await response.json();
        setSchemas(data.schemas);
        if (Object.keys(data.schemas).length > 0) {
          toast.success(`Discovered schemas for ${Object.keys(data.schemas).length} topics`);
        } else {
          toast.info('No schemas discovered yet. Subscribe to topics to start collecting data.');
        }
      }
    } catch (e) {
      toast.error('Failed to fetch schemas');
    }
  };

  // Download script as file
  const handleDownloadScript = () => {
    if (!generatedScript) return;
    const blob = new Blob([generatedScript], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${flowName.replace(/\s+/g, '_').toLowerCase()}.js`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Script downloaded');
  };

  // ========== Flow Save/Load ==========
  const handleSaveFlow = async () => {
    try {
      const flowData = buildFlowPayload();
      const id = routeFlowId || flowId;
      const savedFlow = id
        ? await updatePipeline(id, flowData)
        : await createPipeline(flowData);
      if (!id) {
        setFlowId(savedFlow.id);
      }
      setIsEditingName(false);
      toast.success(`Flow "${flowName}" saved successfully`);
    } catch (e) {
      toast.error('Failed to save flow');
    }
  };

  const handleNewFlow = () => {
    setNodes([]);
    setEdges([]);
    setFlowName('Untitled Flow');
    setFlowId(null);
    setIsEditingName(true);
    setSelectedNode(null);
    setLiveMessages([]);
    toast.success('New flow created');
  };

  const handleLoadFlows = async () => {
    try {
      const data = await listPipelines();
      setSavedFlows(data);
      setLoadFlowModalOpen(true);
    } catch (e) {
      toast.error('Failed to load flows');
    }
  };

  const handleLoadFlow = async (flowSummary) => {
    try {
      const flow = await getPipeline(flowSummary.id);
      applyFlowData(flow);
      setLoadFlowModalOpen(false);
      toast.success(`Loaded flow: ${flow.name}`);
    } catch (e) {
      toast.error('Failed to load flow');
    }
  };

  // ========== Flow Validation ==========
  const isFlowInputNode = (node) => {
    const nodeType = node.data?.nodeType;
    return nodeType === 'mqttInput' || nodeType === 'httpInput' || nodeType === 'kGateway' || node.type === 'gateway';
  };

  const validateFlow = (canvasNodes = nodes, canvasEdges = edges) => {
    const errors = [];

    if (canvasNodes.length === 0) {
      errors.push('Add at least one node to the canvas before running');
      return errors;
    }
    
    const connectedNodes = new Set();
    canvasEdges.forEach(e => {
      connectedNodes.add(e.source);
      connectedNodes.add(e.target);
    });
    
    const disconnectedNodes = canvasNodes.filter((n) => {
      if (canvasNodes.length <= 1) return false;
      if (isFlowInputNode(n)) {
        const resolved = resolveGatewayNodeData(n.data || {}, gateways);
        // Stub gateways without MAC are handled separately; don't double-report
        if ((n.data?.nodeType === 'kGateway' || n.type === 'gateway') && !getGatewayMqttTopic(resolved)) {
          return false;
        }
        const hasDownstream = canvasNodes.some((other) => !isFlowInputNode(other));
        if (!hasDownstream) return false;
        return !canvasEdges.some((e) => e.source === n.id);
      }
      return !connectedNodes.has(n.id);
    });
    if (disconnectedNodes.length > 0) {
      errors.push(`${disconnectedNodes.length} disconnected node(s): ${disconnectedNodes.map(n => n.data?.title || n.data?.name || n.id).join(', ')}`);
    }
    
    canvasNodes.forEach(n => {
      const nodeType = n.data?.nodeType;
      if (nodeType === 'mqttInput' && !n.data?.topic) {
        errors.push(`MQTT Input node "${n.data?.title || n.id}" is missing topic`);
      }
      if ((nodeType === 'kGateway' || n.type === 'gateway')) {
        const resolved = resolveGatewayNodeData(n.data || {}, gateways);
        if (!getGatewayMqttTopic(resolved)) {
          const label = resolved.name && resolved.name !== 'KGateway' ? resolved.name : (n.id || 'Gateway');
          if (!gateways.length) {
            errors.push('No gateways on your account — add a gateway in Gateways first');
          } else {
            errors.push(
              `Gateway "${label}" needs a MAC — open it and pick from "Select Existing Gateway", or delete the empty KGateway node and use the auto-placed gateway from your account`,
            );
          }
        }
      }
      if (nodeType === 'httpInput') {
        const mode = n.data?.mode || 'poll';
        if (mode === 'poll' && !n.data?.url) {
          errors.push(`HTTP Input "${n.data?.title || n.id}" is missing URL`);
        }
        if (mode === 'webhook' && !n.data?.webhookPath) {
          errors.push(`HTTP Input "${n.data?.title || n.id}" is missing webhook path`);
        }
      }
      if (nodeType === 'redisSink' && !n.data?.keyPattern) {
        errors.push(`Redis Sink "${n.data?.title || n.id}" is missing key pattern`);
      }
      if (nodeType === 'supabaseSink' && (!n.data?.supabaseUrl || !n.data?.table)) {
        errors.push(`Supabase Sink "${n.data?.title || n.id}" is missing URL or table name`);
      }
      if (nodeType === 'webhook' && !n.data?.url) {
        errors.push(`Webhook "${n.data?.title || n.id}" is missing URL`);
      }
      if (nodeType === 'qstashPublish' && !n.data?.callbackUrl) {
        errors.push(`QStash "${n.data?.title || n.id}" is missing callback URL`);
      }
    });
    
    // Check for no input nodes
    const inputNodes = canvasNodes.filter(isFlowInputNode);
    if (inputNodes.length === 0) {
      errors.push('Flow has no input node (Gateway, MQTT In, or HTTP In)');
    }

    const needsMqtt = inputNodes.some((n) => {
      const t = n.data?.nodeType;
      return t === 'mqttInput' || t === 'kGateway' || n.type === 'gateway';
    });
    if (needsMqtt) {
      const brokerCfg = isMqttConfigComplete(mqttConfig) ? mqttConfig : getMqttConfigFromGateways(gateways);
      if (!isMqttConfigComplete(brokerCfg)) {
        errors.push('MQTT broker credentials missing — open MQTT settings or ensure your gateway has TBMQ username/password');
      }
    }
    
    return errors;
  };

  const handleValidateAndExecute = async () => {
    const prepared = prepareCanvasForRun(nodes, edges, gateways);
    const canvasChanged =
      JSON.stringify(prepared.nodes) !== JSON.stringify(nodes)
      || JSON.stringify(prepared.edges) !== JSON.stringify(edges);

    if (canvasChanged) {
      setNodes(prepared.nodes);
      setEdges(prepared.edges);
      if (selectedNode) {
        const updated = prepared.nodes.find(
          (n) => n.id === selectedNode.id
            || n.data?.gatewayId === selectedNode.data?.gatewayId,
        );
        if (updated) setSelectedNode(updated);
      }
    }

    const errors = validateFlow(prepared.nodes, prepared.edges);
    if (errors.length > 0) {
      toast.error(errors.join(' • '), { duration: 10000 });
      return;
    }
    await handleExecute(prepared.nodes, prepared.edges);
  };

  // ========== Import Flow from JSON ==========
  const handleImportFlow = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result);
        if (data.flow) {
          handleLoadFlow(data.flow);
        } else if (data.nodes) {
          handleLoadFlow(data);
        } else {
          toast.error('Invalid flow JSON format');
        }
      } catch (err) {
        toast.error('Failed to parse JSON file');
      }
    };
    reader.readAsText(file);
  };

  // ========== Copy/Paste Nodes ==========
  const [clipboard, setClipboard] = useState(null);

  const handleCopyNode = useCallback(() => {
    if (selectedNode) {
      setClipboard({ ...selectedNode, id: null });
      toast.success('Node copied');
    }
  }, [selectedNode]);

  const handlePasteNode = useCallback(() => {
    if (clipboard) {
      const newNode = {
        ...clipboard,
        id: getId(),
        position: {
          x: (clipboard.position?.x || 100) + 50,
          y: (clipboard.position?.y || 100) + 50,
        },
      };
      setNodes(nds => [...nds, newNode]);
      toast.success('Node pasted');
    }
  }, [clipboard]);

  // ========== Undo/Redo ==========
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const maxHistory = 50;

  const saveToHistory = useCallback(() => {
    const state = { nodes: [...nodes], edges: [...edges] };
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(state);
      if (newHistory.length > maxHistory) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, maxHistory - 1));
  }, [nodes, edges, historyIndex]);

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const prevState = history[historyIndex - 1];
      setNodes(prevState.nodes);
      setEdges(prevState.edges);
      setHistoryIndex(prev => prev - 1);
      toast.info('Undo');
    }
  }, [history, historyIndex]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
      setHistoryIndex(prev => prev + 1);
      toast.info('Redo');
    }
  }, [history, historyIndex]);

  // Save to history on node/edge changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (nodes.length > 0 || edges.length > 0) {
        saveToHistory();
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [nodes.length, edges.length]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'c' && selectedNode) {
          e.preventDefault();
          handleCopyNode();
        } else if (e.key === 'v' && clipboard) {
          e.preventDefault();
          handlePasteNode();
        } else if (e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          handleUndo();
        } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
          e.preventDefault();
          handleRedo();
        } else if (e.key === 's') {
          e.preventDefault();
          handleSaveFlow();
        }
      }
      if (e.key === 'Delete' && selectedNode) {
        handleDeleteNode(selectedNode.id);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode, clipboard, handleCopyNode, handlePasteNode, handleUndo, handleRedo]);

  // Update node in flow
  const handleUpdateNode = (nodeId, data, options = {}) => {
    setNodes(nds => nds.map(n => 
      n.id === nodeId ? { ...n, data: { ...n.data, ...data, title: data.label || data.name || n.data.title } } : n
    ));
    setSelectedNode(prev => prev?.id === nodeId ? { ...prev, data: { ...prev.data, ...data } } : prev);
    if (!options.silent) {
      toast.success('Node configuration saved');
    }
  };

  // Delete node
  const handleDeleteNode = (nodeId) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
    toast.info('Node deleted');
  };

  return (
    <div className={`flow-wrapper ${activeTheme}${embedded ? ' flow-wrapper--embedded' : ''}`} data-theme={activeTheme}>
      {/* Show loading first - takes priority */}
      {authLoading || (flowLoading && !pipelineLoadedRef.current) ? (
        <div className="login-container">
          <div className="login-card" style={{ textAlign: 'center' }}>
            <LuRadio className="w-10 h-10 text-cyan-500 animate-pulse mx-auto" />
            <p className="text-zinc-400 mt-4">{flowLoading ? 'Loading pipeline...' : 'Loading...'}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Toolbar */}
          <header className={`toolbar${embedded ? ' toolbar--embedded' : ''}`} data-testid="toolbar">
            <div className="toolbar-left">
              {embedded ? (
                <>
                  {onBack && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onBack}
                      title="Back to pipelines"
                      className="back-to-pipelines-btn"
                      data-testid="back-to-pipelines-btn"
                    >
                      <LuArrowLeft className="h-4 w-4 mr-1" />
                      Pipelines
                    </Button>
                  )}
                  <div className="toolbar-divider" />
                </>
              ) : (
                <>
                  <SiMqtt className="logo-icon" />
                  <span className="logo-text">ChillVerify</span>
                  <div className="toolbar-divider" />
                </>
              )}
              
              {/* Flow Name - Edit/Display Mode */}
              <div className="flow-name-group">
                {isEditingName ? (
                  <>
                    <Input
                      value={flowName}
                      onChange={(e) => setFlowName(e.target.value)}
                      className="flow-name-input"
                      data-testid="flow-name-input"
                      placeholder="Enter flow name..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveFlow();
                        }
                      }}
                      autoFocus
                    />
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={handleSaveFlow} 
                      title="Save Flow"
                      className="flow-name-save-btn"
                      data-testid="flow-name-save-btn"
                    >
                      <LuSave className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flow-name-display" data-testid="flow-name-display">
                      {flowName}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => setIsEditingName(true)} 
                      title="Edit flow name"
                      className="flow-name-edit-btn"
                      data-testid="flow-name-edit-btn"
                    >
                      <LuPencil className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
              
              <div className="toolbar-divider" />
              
              {!embedded && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleNewFlow} 
                  title="Create New Flow"
                  className="new-flow-btn"
                  data-testid="new-flow-btn"
                >
                  <LuFilePlus className="h-4 w-4 mr-1" />
                  New
                </Button>
              )}
            </div>

            <div className="toolbar-center">
              {mqttBrokerOnline && (
                <span className="mqtt-online-badge" data-testid="mqtt-online-badge">
                  <span className="mqtt-online-dot" aria-hidden="true" />
                  MQTT Online
                </span>
              )}
              <Button
                variant={mqttConnected ? 'default' : 'outline'}
                size="sm"
                onClick={openMqttModal}
                className={`mqtt-btn ${mqttConnected ? 'connected' : ''}`}
                data-testid="mqtt-btn"
              >
                {mqttConnected ? <LuWifi className="mr-2 h-4 w-4" /> : <LuWifiOff className="mr-2 h-4 w-4" />}
                {mqttConnected ? 'Connected' : 'Connect MQTT'}
              </Button>
            </div>

            <div className="toolbar-right">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleUndo} 
                title="Undo (Ctrl+Z)"
                disabled={historyIndex <= 0}
                data-testid="undo-btn"
              >
                <LuUndo2 className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleRedo} 
                title="Redo (Ctrl+Y)"
                disabled={historyIndex >= history.length - 1}
                data-testid="redo-btn"
              >
                <LuRedo2 className="h-4 w-4" />
              </Button>
              <div className="toolbar-divider" />
              {!embedded && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={toggleTheme} 
                  title={activeTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  data-testid="theme-toggle-btn"
                >
                  {activeTheme === 'dark' ? <LuSun className="h-4 w-4" /> : <LuMoon className="h-4 w-4" />}
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={handleSaveFlow} title="Save Flow (Ctrl+S)" data-testid="save-btn">
                <LuSave className="h-4 w-4" />
              </Button>
              {!embedded && (
                <Button variant="ghost" size="icon" onClick={handleLoadFlows} title="Load Flow" data-testid="load-btn">
                  <LuFolderOpen className="h-4 w-4" />
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={() => setExportModalOpen(true)} title="Export" data-testid="export-btn">
                <LuDownload className="h-4 w-4" />
              </Button>
              <Button 
                variant={liveViewOpen ? 'default' : 'ghost'} 
                size="icon" 
                onClick={() => setLiveViewOpen(!liveViewOpen)} 
                title="Live Messages"
                className={liveViewOpen ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                data-testid="live-view-btn"
              >
                <LuRadio className="h-4 w-4" />
              </Button>
              <div className="toolbar-divider" />
              <Button
                size="sm"
                onClick={handleValidateAndExecute}
                disabled={isStartingFlow}
                className={`execute-btn ${isExecuting ? 'running' : ''}`}
                data-testid="execute-btn"
              >
                {isStartingFlow ? (
                  <LuLoader className="mr-2 h-4 w-4 animate-spin" />
                ) : isExecuting ? (
                  <LuSquare className="mr-2 h-4 w-4" />
                ) : (
                  <LuPlay className="mr-2 h-4 w-4" />
                )}
                {isStartingFlow ? 'Starting…' : isExecuting ? 'Stop' : 'Run'}
              </Button>
              {!embedded && (
                <>
                  <div className="toolbar-divider" />
                  <div className="user-menu">
                    <LuUser className="w-4 h-4 text-zinc-400" />
                    <span className="user-email">{user?.email}</span>
                    <button 
                      className="user-logout-btn"
                      onClick={logout}
                      title="Sign out"
                    >
                      <LuLogOut className="w-3 h-3" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </header>

      {/* Main Content */}
      <div className="etl-flow-main">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

        <div className="canvas-wrapper" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes.map(n => ({
              ...n,
              data: {
                ...n.data,
                activity: activeNodes[n.id],
                hasError: !!nodeErrors[n.id],
              }
            }))}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            fitView
            snapToGrid
            snapGrid={[16, 16]}
            colorMode={activeTheme}
            proOptions={{ hideAttribution: true }}
            data-testid="react-flow-canvas"
          >
            <Background color={activeTheme === 'dark' ? '#444' : '#cbd5e1'} gap={20} size={1.5} variant="dots" />
            <Controls showInteractive={false} />
            <MiniMap 
              nodeColor={(n) => n.data?.color || '#06b6d4'}
              maskColor={activeTheme === 'dark' ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.85)'}
            />
            {/* SVG Definitions for edge gradients */}
            <svg>
              <defs>
                <linearGradient id="edge-gradient">
                  <stop offset="0%" stopColor="#ae53ba" />
                  <stop offset="100%" stopColor="#2a8af6" />
                </linearGradient>
                <marker
                  id="edge-circle"
                  viewBox="-5 -5 10 10"
                  refX="0"
                  refY="0"
                  markerUnits="strokeWidth"
                  markerWidth="10"
                  markerHeight="10"
                  orient="auto"
                >
                  <circle stroke="#2a8af6" strokeOpacity="0.75" r="2" cx="0" cy="0" />
                </marker>
              </defs>
            </svg>
          </ReactFlow>
        </div>

        {selectedNode && (
          <ConfigPanel
            node={selectedNode}
            onClose={() => setSelectedNode(null)}
            onUpdate={handleUpdateNode}
            onDelete={handleDeleteNode}
            liveMessages={liveMessages}
            mqttConnected={mqttConnected}
            nodes={nodes}
            edges={edges}
          />
        )}

        {/* Live Messages Panel */}
        {liveViewOpen && (
          <div className="live-panel" data-testid="live-panel">
            <div className="live-panel-header">
              <div className="flex items-center gap-2">
                <LuRadio className={`w-4 h-4 ${isExecuting ? 'text-emerald-500 animate-pulse' : 'text-zinc-500'}`} />
                <span className="live-panel-title">Live Data</span>
                {isExecuting && <span className="live-badge">LIVE</span>}
              </div>
              <div className="flex items-center gap-2">
                {/* View Mode Toggle */}
                <div className="live-view-toggle" data-testid="live-view-toggle">
                  <button 
                    className={`toggle-btn ${liveViewMode === 'json' ? 'active' : ''}`}
                    onClick={() => setLiveViewMode('json')}
                    title="JSON View"
                  >
                    <LuBraces className="w-4 h-4" />
                  </button>
                  <button 
                    className={`toggle-btn ${liveViewMode === 'table' ? 'active' : ''}`}
                    onClick={() => setLiveViewMode('table')}
                    title="Table View"
                  >
                    <LuTable className="w-4 h-4" />
                  </button>
                </div>
                {/* Clear Messages */}
                {liveMessages.length > 0 && (
                  <button 
                    className="live-panel-clear" 
                    onClick={() => setLiveMessages([])}
                    title="Clear messages"
                  >
                    <LuTrash2 className="w-4 h-4" />
                  </button>
                )}
                <button className="live-panel-close" onClick={() => setLiveViewOpen(false)}>
                  <LuX className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {liveMessages.length === 0 ? (
              <div className="live-empty">
                <p>No messages yet</p>
                <p className="text-xs">Start the flow to see live data</p>
              </div>
            ) : liveViewMode === 'table' ? (
              <LiveDataTable messages={liveMessages} />
            ) : (
              <ScrollArea className="live-panel-content">
                <div className="live-messages">
                  {liveMessages.map((msg, i) => (
                    <div key={i} className="live-message">
                      <div className="live-message-header">
                        <span className="live-message-topic">{msg.topic}</span>
                        <span className="live-message-time">{new Date(msg.timestamp).toLocaleTimeString()}</span>
                      </div>
                      <pre className="live-message-payload">{JSON.stringify(msg.payload, null, 2)}</pre>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        )}
      </div>

      <MqttBrokerModal
        open={mqttModalOpen}
        onOpenChange={setMqttModalOpen}
        mqttConfig={mqttConfig}
        onConfigChange={setMqttConfig}
        onConnected={() => {
          setMqttConnected(true);
          setMqttBrokerOnline(true);
        }}
      />

      {/* Export Modal */}
      <Dialog open={exportModalOpen} onOpenChange={setExportModalOpen}>
        <DialogContent className="modal-content modal-lg" data-testid="export-modal">
          <DialogHeader>
            <DialogTitle className="modal-title">Export Flow</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="json">
            <TabsList className="modal-tabs">
              <TabsTrigger value="json">JSON</TabsTrigger>
              <TabsTrigger value="script" onClick={handleGenerateScript}>Node.js</TabsTrigger>
              <TabsTrigger value="schema" onClick={handleFetchSchemas}>Schema</TabsTrigger>
              <TabsTrigger value="import">Import</TabsTrigger>
            </TabsList>
            <TabsContent value="json">
              <ScrollArea className="export-preview">
                <pre className="export-code">{exportJSON()}</pre>
              </ScrollArea>
              <div className="export-actions">
                <Button variant="outline" onClick={handleCopy}>
                  {copied ? <LuCheck className="mr-2 h-4 w-4" /> : <LuCopy className="mr-2 h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </TabsContent>
            <TabsContent value="script">
              <ScrollArea className="export-preview">
                <pre className="export-code">{generatedScript || '// Click this tab to generate Node.js script'}</pre>
              </ScrollArea>
              <div className="export-actions">
                <Button variant="outline" onClick={handleCopyScript} disabled={!generatedScript}>
                  {copied ? <LuCheck className="mr-2 h-4 w-4" /> : <LuCopy className="mr-2 h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                <Button variant="outline" onClick={handleDownloadScript} disabled={!generatedScript}>
                  <LuDownload className="mr-2 h-4 w-4" />
                  Download .js
                </Button>
              </div>
            </TabsContent>
            <TabsContent value="schema">
              <ScrollArea className="export-preview">
                {Object.keys(schemas).length > 0 ? (
                  <div className="schema-list">
                    {Object.entries(schemas).map(([topic, schema]) => (
                      <div key={topic} className="schema-item">
                        <h4 className="schema-topic">{topic}</h4>
                        <pre className="export-code">{JSON.stringify(schema, null, 2)}</pre>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="schema-empty">
                    <p>No schemas discovered yet.</p>
                    <p className="config-hint">Connect to MQTT and subscribe to topics to start discovering message schemas automatically.</p>
                  </div>
                )}
              </ScrollArea>
              <div className="export-actions">
                <Button variant="outline" onClick={handleFetchSchemas}>
                  Refresh Schemas
                </Button>
              </div>
            </TabsContent>
            <TabsContent value="import">
              <div className="import-section">
                <p className="config-hint">Import a previously exported flow JSON file</p>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportFlow}
                  className="import-file-input"
                  id="import-flow-file"
                />
                <label htmlFor="import-flow-file" className="import-file-label">
                  <LuUpload className="mr-2 h-4 w-4" />
                  Choose JSON File
                </label>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Load Flow Modal */}
      {!embedded && (
      <Dialog open={loadFlowModalOpen} onOpenChange={setLoadFlowModalOpen}>
        <DialogContent className="modal-content load-modal-content" data-testid="load-modal">
          <DialogHeader>
            <DialogTitle className="modal-title">
              <LuFolderOpen className="inline mr-2 h-5 w-5" />
              Load Saved Flow
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="load-flow-list">
            {savedFlows.length > 0 ? (
              savedFlows.map((flow, idx) => (
                <div 
                  key={flow.id || idx} 
                  className="load-flow-item"
                  data-testid={`load-flow-item-${idx}`}
                >
                  <div 
                    className="load-flow-info"
                    onClick={() => handleLoadFlow(flow)}
                  >
                    <div className="load-flow-name">{flow.name || 'Untitled Flow'}</div>
                    <div className="load-flow-meta">
                      <span>{flow.node_count ?? flow.nodes?.length ?? 0} nodes</span>
                      {(flow.created_at || flow.savedAt) && (
                        <>
                          <span className="load-flow-dot">•</span>
                          <span>{new Date(flow.created_at || flow.savedAt).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="load-flow-delete"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await deletePipeline(flow.id);
                        setSavedFlows(savedFlows.filter((_, i) => i !== idx));
                        toast.success('Flow deleted');
                      } catch (err) {
                        toast.error('Failed to delete flow');
                      }
                    }}
                    title="Delete flow"
                    data-testid={`delete-flow-btn-${idx}`}
                  >
                    <LuTrash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            ) : (
              <div className="load-flow-empty">
                <LuFolderOpen className="h-12 w-12 opacity-30 mb-4" />
                <p>No saved flows found</p>
                <p className="text-sm opacity-60 mt-1">Save a flow first using the save button</p>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
      )}
        </>
      )}
    </div>
  );
}

export default function FlowEditor({ embedded = false, flowId, onBack }) {
  return (
    <ReactFlowProvider>
      <DnDProvider>
        <Toaster position="top-right" richColors closeButton duration={5000} />
        <Flow embedded={embedded} flowId={flowId} onBack={onBack} />
      </DnDProvider>
    </ReactFlowProvider>
  );
}
